import { Component, OnChanges, SimpleChanges, computed, effect, inject, input, output, signal } from '@angular/core';
import { Validators } from '@angular/forms';

import { Observable, take } from 'rxjs';

import { FormComponent } from '@camelot/form-basic';
import { InputAddressComponent } from '@camelot/form-input';
import { InputAddress, InputPanel, InputTextBox } from '@camelot/form-model';
import { FontIconComponent } from '@camelot/icons';
import { CamAddressService } from '@camelot/services';
import { TranslatePipe } from '@camelot/translation';
import {
  BadgeComponent,
  ButtonComponent,
  ErrorComponent,
  LinkComponent,
  LoaderComponent,
  TextComponent,
  TitleComponent,
} from '@camelot/ui';
import { CamAbstractComponent } from '@camelot/utils';

import { MapComponent } from '../../features/maps/components/map/map.component';
import { CamTranslationCore } from '../../translation.service';

interface AddressSnapshot {
  country: string;
  latitude: number;
  locality: string;
  longitude: number;
  placeId: string;
  postalCode: string;
  street: string;
}
export interface IAddressValue {
  complement?: string;
  country: string;
  latitude: number;
  locality: string;
  longitude: number;
  placeId?: string;
  postalCode: number;
  street: string;
  streetNumber: number;
}
type AddressFormState = 'loading' | 'empty' | 'locked' | 'manual';

@Component({
  selector: 'cam-address-form',
  imports: [
    BadgeComponent,
    ButtonComponent,
    ErrorComponent,
    FontIconComponent,
    FormComponent,
    LinkComponent,
    LoaderComponent,
    MapComponent,
    TextComponent,
    TitleComponent,
    TranslatePipe,
    InputAddressComponent,
  ],
  templateUrl: './address-form.component.html',
  styleUrl: './address-form.component.scss',
})
export class AddressFormComponent extends CamAbstractComponent implements OnChanges {
  addressId = input<string>();

  askValidation$ = input.required<Observable<null>>();

  isFormValid = output<boolean>();

  saved = output<string>();

  private readonly _addressService = inject(CamAddressService);

  private _snapshot = signal<AddressSnapshot | null>(null);
  private _formInternalValid = signal<boolean>(false);

  public state = signal<AddressFormState>('empty');
  public fetchError = signal<boolean>(false);
  public currentPlaceId = computed(() => this._snapshot()?.placeId ?? '');
  public hasSnapshot = computed(() => this._snapshot() !== null);
  public mapPoints = computed(() => {
    const snap = this._snapshot();
    if (this.state() !== 'locked' || !snap || !snap.latitude || !snap.longitude) {
      return [];
    }
    return [
      {
        lat: snap.latitude,
        lng: snap.longitude,
        title: `${snap.street}, ${snap.postalCode} ${snap.locality}`.trim(),
      },
    ];
  });

  public googleAddressInput = new InputAddress({
    key: 'googleAddress',
    label: 'core.address-form.search-google',
  });
  public streetInput = new InputTextBox({
    readonly: true,
    key: 'displayStreet',
    label: 'core.address-form.street',
    validators: [Validators.required],
  });
  public postalCodeInput = new InputTextBox({
    class: 'col-4',
    readonly: true,
    key: 'displayPostalCode',
    label: 'core.address-form.postal-code',
    validators: [Validators.required],
  });
  public localityInput = new InputTextBox({
    class: 'col-8',
    readonly: true,
    key: 'displayLocality',
    label: 'core.address-form.locality',
    validators: [Validators.required],
  });
  public countryInput = new InputTextBox({
    readonly: true,
    key: 'displayCountry',
    label: 'core.address-form.country',
    validators: [Validators.required],
  });
  public complementInput = new InputTextBox({
    key: 'complement',
    label: 'core.address-form.complement',
  });

  private _detailsInputs: InputTextBox<string>[] = [
    this.streetInput,
    this.postalCodeInput,
    this.localityInput,
    this.countryInput,
  ];

  public formInputs = [
    new InputPanel({
      children: [this.streetInput, this.postalCodeInput, this.localityInput, this.countryInput, this.complementInput],
      contentClass: 'row',
      key: 'details-panel',
    }),
  ];

  constructor() {
    super();
    CamTranslationCore.getInstance();
    effect(() => {
      const state = this.state();
      let effective: boolean;
      switch (state) {
        case 'empty':
        case 'loading':
          effective = false;
          break;
        case 'locked':
          effective = true;
          break;
        case 'manual':
          effective = this._formInternalValid();
          break;
      }
      this.isFormValid.emit(effective);
    });
  }

  public onCamFormValidityChange(valid: boolean) {
    this._formInternalValid.set(valid);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['addressId']) {
      this._handleAddressIdChange();
    }
  }

  public onGooglePlaceSelected(data: IAddressValue) {
    if (!data?.placeId) {
      return;
    }

    const street = `${data.street ?? ''} ${data.streetNumber ?? ''}`.trim();
    const snapshot: AddressSnapshot = {
      country: data.country ?? '',
      latitude: data.latitude ?? 0,
      locality: data.locality ?? '',
      longitude: data.longitude ?? 0,
      placeId: data.placeId,
      postalCode: String(data.postalCode ?? ''),
      street,
    };

    this._snapshot.set(snapshot);
    this._applySnapshotToFields(snapshot);
    this._setDetailsLocked(true);
    this.state.set('locked');
  }

  public unlockManualMode() {
    this.state.set('manual');
    this._setDetailsLocked(false);
  }

  public revertToOriginal() {
    const snapshot = this._snapshot();
    if (!snapshot) {
      return;
    }
    this._applySnapshotToFields(snapshot);
    this._setDetailsLocked(true);
    this.state.set('locked');
  }

  public submit() {
    if (this.state() === 'loading' || this.requestState.isLoading()) {
      return;
    }

    const matchesSnapshot = this._matchesSnapshot();
    const snapshot = this._snapshot();

    const payload = {
      city: this.localityInput.value ?? '',
      complement: this.complementInput.value ?? '',
      countryCode: this.countryInput.value ?? '',
      latitude: matchesSnapshot && snapshot ? snapshot.latitude : null,
      longitude: matchesSnapshot && snapshot ? snapshot.longitude : null,
      placeId: matchesSnapshot && snapshot ? snapshot.placeId : null,
      postalCode: (this.postalCodeInput.value ?? '').toString(),
      street: (this.streetInput.value ?? '').toString(),
    };

    this.requestState.asked();

    const addressId = this.addressId();
    const operation$ = addressId
      ? this._addressService.updateAddress$({ ...payload, id: addressId })
      : this._addressService.addAddress$(payload);

    this._registerSubscription(
      operation$.pipe(take(1)).subscribe({
        error: () => {
          this.requestState.completed();
        },
        next: address => {
          this.requestState.completed();
          this.saved.emit(address.id);
        },
      })
    );
  }

  private _handleAddressIdChange() {
    const addressId = this.addressId();
    if (!addressId) {
      this._snapshot.set(null);
      this._clearDetails();
      this._setDetailsLocked(true);
      this.fetchError.set(false);
      this.state.set('empty');
      return;
    }

    this.fetchError.set(false);
    this.state.set('loading');

    this._registerSubscription(
      this._addressService.fetchAddress$(addressId).subscribe({
        error: () => {
          this.fetchError.set(true);
          this.state.set('empty');
        },
        next: address => {
          const snapshot: AddressSnapshot = {
            country: address.countryCode ?? '',
            latitude: address.latitude ?? 0,
            locality: address.city ?? '',
            longitude: address.longitude ?? 0,
            placeId: address.placeId ?? '',
            postalCode: address.postalCode ?? '',
            street: address.street ?? '',
          };
          this._snapshot.set(snapshot);
          this._applySnapshotToFields(snapshot);
          this.complementInput.value = address.complement ?? '';
          this._setDetailsLocked(true);
          this.state.set('locked');
        },
      })
    );
  }

  private _applySnapshotToFields(snapshot: AddressSnapshot) {
    this.streetInput.value = snapshot.street;
    this.postalCodeInput.value = snapshot.postalCode;
    this.localityInput.value = snapshot.locality;
    this.countryInput.value = snapshot.country;
  }

  private _clearDetails() {
    this.streetInput.value = '';
    this.postalCodeInput.value = '';
    this.localityInput.value = '';
    this.countryInput.value = '';
    this.complementInput.value = '';
  }

  private _setDetailsLocked(locked: boolean) {
    this._detailsInputs.forEach(input => {
      input.setReadonly(locked);
    });
  }

  private _matchesSnapshot(): boolean {
    const snap = this._snapshot();
    if (!snap) {
      return false;
    }
    return (
      this.streetInput.value === snap.street &&
      this.postalCodeInput.value === snap.postalCode &&
      this.localityInput.value === snap.locality &&
      this.countryInput.value === snap.country
    );
  }
}
